/**
 * Turns what a shopper types into a query Shopify's search can actually answer.
 *
 * Shopify ORs search terms by default, so every extra word a shopper types makes the results
 * worse instead of better. On this catalog "drz125 chain" returned 366 products and not one
 * was a DR-Z125 part: the token "drz125" matches nothing (the catalog spells it "DR-Z125"),
 * so all 366 came from the word "chain" on its own, chainsaw chain included.
 *
 * Two fixes, both applied here:
 *
 *   1. Join the terms with AND, so more words means fewer, better results.
 *   2. Expand model codes to the spellings the catalog actually uses, so "drz125" reaches
 *      "DR-Z125". Separators at a letter/digit boundary are generated on the spot; the ones
 *      that sit inside a run of letters come from search-synonyms.json, which is mined from
 *      the catalog by build-search-synonyms.ts in Syncshop.
 *
 * "drz125 chain" becomes "(drz125 OR dr-z125) AND chain" -> 5 results, all correct.
 *
 * The shopper never sees this. The typed words are carried along in `oq` and put back into
 * the heading and the search box on arrival, so the page still reads "drz125 chain".
 */
(function () {
  "use strict";

  var SYNONYM_ASSET = document.documentElement.dataset.searchSynonymsUrl || "";
  var synonyms = null;
  var loading = null;

  /**
   * Fetched on first focus rather than on page load: it is ~45 KB and only matters to someone
   * who is about to search. If it has not arrived by the time they hit enter, the query is
   * still ANDed and still boundary-expanded - the synonyms only add spellings.
   */
  function load() {
    if (synonyms || !SYNONYM_ASSET) return Promise.resolve();
    if (loading) return loading;
    loading = fetch(SYNONYM_ASSET)
      .then(function (r) {
        return r.ok ? r.json() : {};
      })
      .then(function (map) {
        synonyms = map;
      })
      .catch(function () {
        synonyms = {};
      });
    return loading;
  }

  var OPERATORS = /(\bAND\b|\bOR\b|\bNOT\b|[():*]|\w+:)/;

  function stripPunctuation(token) {
    return token.replace(/^[^\w]+|[^\w]+$/g, "");
  }

  /**
   * Spellings derivable from the token itself: a separator at each letter/digit boundary.
   * "rzr900" -> "rzr 900", "rzr-900". Keeping these out of the shipped map is what holds it
   * to 45 KB instead of 1.4 MB.
   */
  function boundaryForms(token) {
    var forms = [];
    var parts = token.match(/[a-z]+|[0-9]+/g);
    if (parts && parts.length > 1) {
      forms.push(parts.join(" "), parts.join("-"));
    }
    return forms;
  }

  function expandToken(token) {
    var bare = stripPunctuation(token).toLowerCase();
    if (!bare) return null;

    // Only model-code-shaped tokens get expanded. A plain word has one spelling.
    if (!/[a-z]/.test(bare) || !/[0-9]/.test(bare)) return token;

    var key = bare.replace(/[^a-z0-9]/g, "");
    var forms = [bare];
    var mapped = synonyms && synonyms[key];
    if (mapped) forms = forms.concat(mapped);
    if (key !== bare) forms.push(key);
    forms = forms.concat(boundaryForms(key));

    var seen = {};
    var unique = forms.filter(function (f) {
      if (!f || seen[f]) return false;
      seen[f] = true;
      return true;
    });

    if (unique.length === 1) return token;
    return (
      "(" +
      unique
        .map(function (f) {
          return /\s/.test(f) ? '"' + f + '"' : f;
        })
        .join(" OR ") +
      ")"
    );
  }

  /**
   * A model code or part number - "drz125", "25-1800". High signal: these narrow hard.
   *
   * A bare number is not one. Shoppers split a model across tokens ("rzr 900 shock"), and
   * treating the "900" as the identifier made it the required term and demoted "rzr" to an
   * optional one. Requiring a letter alongside the digits, or punctuation as in a part number,
   * keeps a loose number as an ordinary describing word.
   */
  function isIdentifier(chunk) {
    var bare = stripPunctuation(chunk).toLowerCase();
    if (bare.length < 2 || !/[0-9]/.test(bare)) return false;
    return /[a-z]/.test(bare) || /[-.]/.test(bare);
  }

  /**
   * Returns the refined query, or the input unchanged when there is nothing to gain -
   * an empty box, or a query where the shopper has written their own operators.
   *
   * `rung` walks a ladder from strict to loose, used to retry a search that found nothing
   * rather than dead-ending the shopper. 0 is what a search starts with.
   */
  function refine(raw, rung) {
    var term = (raw || "").trim();
    if (!term || OPERATORS.test(term)) return term;

    // Quoted phrases are a deliberate instruction; pass them through as single units.
    var chunks = term.match(/"[^"]*"|\S+/g);
    if (!chunks || chunks.length === 0) return term;

    var identifiers = [];
    var descriptors = [];
    chunks.forEach(function (chunk) {
      var piece = chunk.charAt(0) === '"' ? chunk : expandToken(chunk);
      if (!piece) return;
      (isIdentifier(chunk) ? identifiers : descriptors).push(piece);
    });

    if (identifiers.length === 0 && descriptors.length === 0) return term;

    // Rung 2 is the shopper's own words - Shopify's plain OR. Broad, but never empty.
    if (rung >= 2) return term;

    // Rung 1 drops the describing words and keeps the identifier. "(ltz90 OR lt-z90)" is
    // 5 products out of 37,000, which still beats showing nothing.
    if (rung >= 1 && identifiers.length) return identifiers.join(" AND ");

    // With no identifier every word carries equal weight, so AND them all: "oil filter suzuki"
    // goes from over 1000 results to 22 that way.
    if (identifiers.length === 0) return descriptors.join(" AND ");

    var required = identifiers.join(" AND ");
    if (descriptors.length === 0) return required;

    /**
     * An identifier already does the narrowing, so the describing words only have to match one
     * of themselves. ANDing all of them on top is what turns a good search into no results:
     * shoppers write "brake pads" where the catalog says "BRAKE PAD", and
     * "(ltz90 OR lt-z90) AND brake AND pads" finds nothing while
     * "(ltz90 OR lt-z90) AND (brake OR pads)" finds the pad.
     */
    var describing =
      descriptors.length > 1 ? "(" + descriptors.join(" OR ") + ")" : descriptors[0];
    return required + " AND " + describing;
  }

  function isSearchForm(form) {
    if (!form || form.tagName !== "FORM") return false;
    var action = form.getAttribute("action") || "";
    return form.getAttribute("role") === "search" || /\/search/.test(action);
  }

  document.addEventListener(
    "focusin",
    function (event) {
      var el = event.target;
      if (el && el.name === "q" && isSearchForm(el.form)) load();
    },
    true
  );

  document.addEventListener(
    "submit",
    function (event) {
      var form = event.target;
      if (!isSearchForm(form)) return;

      var input = form.querySelector('input[name="q"]');
      if (!input || !input.value.trim()) return;

      var typed = input.value.trim();
      var refined = refine(typed);
      if (refined === typed) return;

      // The typed words ride along so the results page can show them back.
      var original = form.querySelector('input[name="oq"]');
      if (!original) {
        original = document.createElement("input");
        original.type = "hidden";
        original.name = "oq";
        form.appendChild(original);
      }
      original.value = typed;
      input.value = refined;

      // Put the box back the way they left it, in case the browser stays on the page.
      setTimeout(function () {
        input.value = typed;
      }, 0);
    },
    true
  );

  /**
   * On the results page, show the shopper their own words rather than the machinery.
   * Also relaxes the query once if ANDing turned out to be too strict, so a search can
   * degrade to the old broad behaviour instead of dead-ending on "no results".
   */
  function restoreDisplayedTerms() {
    var params = new URLSearchParams(window.location.search);
    var typed = params.get("oq");
    if (!typed) return;

    document.querySelectorAll('input[name="q"]').forEach(function (input) {
      if (input.type !== "hidden") input.value = typed;
    });

    var refined = params.get("q") || "";
    if (!refined || refined === typed) return;

    // The count heading and the empty-state caption are the only places the query is printed
    // back. Swapping just those beats walking the page and risking unrelated text.
    var printed = document.querySelectorAll(
      "#ProductCount, .search-empty-state-caption, .template-search-header h1"
    );
    printed.forEach(function (el) {
      var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      var node;
      while ((node = walker.nextNode())) {
        if (node.nodeValue.indexOf(refined) !== -1) {
          node.nodeValue = node.nodeValue.split(refined).join(typed);
        }
      }
    });
    if (document.title.indexOf(refined) !== -1) {
      document.title = document.title.split(refined).join(typed);
    }
  }

  /**
   * A narrowed search that found nothing is worse than a broad one. When that happens, step
   * down the ladder once and reload: strict -> identifier only -> the shopper's own words.
   * `rung` is carried in the URL so this can never loop.
   */
  function relaxIfEmpty() {
    var params = new URLSearchParams(window.location.search);
    var typed = params.get("oq");
    if (!typed) return;
    if (!document.querySelector(".template-search-empty")) return;

    var rung = parseInt(params.get("rung") || "0", 10) + 1;
    if (rung > 2) return;

    var next = refine(typed, rung);
    if (!next || next === params.get("q")) {
      if (rung === 2) return;
      next = refine(typed, 2);
      rung = 2;
    }

    params.set("q", next);
    params.set("rung", String(rung));
    window.location.replace(window.location.pathname + "?" + params.toString());
  }

  function onReady() {
    restoreDisplayedTerms();
    // The ladder rebuilds the query, so it needs the same spellings the first attempt had.
    if (new URLSearchParams(window.location.search).get("oq")) {
      load().then(relaxIfEmpty);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", onReady);
  } else {
    onReady();
  }

  window.ShopSearchQuery = { refine: refine, load: load };
})();
